import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Button, Tooltip } from "antd";
import { FileExcelOutlined, DownloadOutlined, LoadingOutlined } from "@ant-design/icons";
import queryString from "query-string";

import { initiateItemsExport, checkExportStatus, downloadExportFile, setExportStatus } from "store/item/actions";
import { setFetchMessage } from "store/helper/actions";

const ExportItems = ({ columns }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  
  const { authObject } = useSelector(({ authReducer }) => authReducer);
  const { exportStatus, exportLoading } = useSelector(({ itemReducer }) => itemReducer);
  
  const [isPolling, setIsPolling] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [exportStarted, setExportStarted] = useState(false);

  const searchParams = queryString.parse(location.search);

  useEffect(() => {
    if (authObject?._id && authObject?.token) {
      dispatch(checkExportStatus(authObject._id));
    }
  }, [dispatch, authObject]);

  useEffect(() => {
    if (isPolling && authObject?._id) {
      const interval = setInterval(() => {
        dispatch(checkExportStatus(authObject._id));
      }, 2000);
      
      setPollingInterval(interval);
      
      return () => {
        clearInterval(interval);
      };
    } else if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPolling, authObject, dispatch]);

  useEffect(() => {
    if (exportStatus === 'completed' || exportStatus === 'failed' || exportStatus === 'not_found' || exportStatus === null) {
      setIsPolling(false);
    }
  }, [exportStatus]);

  useEffect(() => {
    if (exportStatus === 'completed' && exportStarted) {
      dispatch(setFetchMessage({
        type: 'SUCCESS_STATUS',
        severity: 'success',
        message: 'Your export is ready. Click Download to save the file.'
      }));
      setExportStarted(false);
    }
  }, [exportStatus, exportStarted, dispatch]);

  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const handleExportClick = () => {
    if (columns && columns.length > 0) {
      dispatch(setExportStatus(null));
      dispatch(initiateItemsExport(searchParams, columns));
      setIsPolling(true);
      setExportStarted(true);
    }
  };

  const handleDownloadClick = () => {
    if (authObject?._id) {
      dispatch(downloadExportFile(authObject._id));
    }
  };

  const getExportButtonContent = () => {
    if (exportLoading) {
      return (
        <>
          <LoadingOutlined />
          <span style={{ marginLeft: 8 }}>Exporting...</span>
        </>
      );
    }

    if (exportStatus === 'processing' || isPolling) {
      return (
        <>
          <LoadingOutlined />
          <span style={{ marginLeft: 8 }}>Processing...</span>
        </>
      );
    }

    if (exportStatus === 'failed') {
      return (
        <>
          <FileExcelOutlined />
          <span style={{ marginLeft: 8 }}>Retry Export</span>
        </>
      );
    }

    return (
      <>
        <FileExcelOutlined />
        <span style={{ marginLeft: 8 }}>Export</span>
      </>
    );
  };

  const getExportTooltipTitle = () => {
    if (exportLoading) return "Exporting items...";
    if (exportStatus === 'processing') return "Export is being processed...";
    if (exportStatus === 'failed') return "Export failed. Click to retry";
    return "Export items to Excel with current filters and columns";
  };

  const getDownloadTooltipTitle = () => {
    return "Download the exported file";
  };

  const isExportButtonDisabled = () => {
    return exportLoading || (exportStatus === 'processing') || (!columns || columns.length === 0);
  };

  const isDownloadButtonDisabled = () => {
    return exportStatus !== 'completed' || exportLoading || exportStatus === 'processing' || isPolling;
  };

  return (
    <div style={{ marginLeft: 8, display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Tooltip title={getExportTooltipTitle()}>
        <Button
          type="primary"
          onClick={handleExportClick}
          disabled={isExportButtonDisabled()}
          loading={exportLoading || (exportStatus === 'processing')}
        >
          {getExportButtonContent()}
        </Button>
      </Tooltip>

      {(exportStatus === 'completed' || (exportStatus && exportStatus !== 'processing' && exportStatus !== 'failed' && exportStatus !== 'not_found')) && (
        <Tooltip title={getDownloadTooltipTitle()}>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownloadClick}
            disabled={isDownloadButtonDisabled()}
            style={{ 
              backgroundColor: exportStatus === 'completed' ? '#52c41a' : '#d9d9d9',
              borderColor: exportStatus === 'completed' ? '#52c41a' : '#d9d9d9',
              color: exportStatus === 'completed' ? '#fff' : '#00000040'
            }}
          >
            {exportStatus === 'completed' ? 'Download' : 'Download (Processing)'}
          </Button>
        </Tooltip>
      )}
    </div>
  );
};

export default ExportItems;